import React, { useCallback, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  DialogTitle,
} from "@mui/material";
import { IconButton, Typography, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CodeListSection from "./CodeListSection";
import {
  COVERAGE_CRITERIA_ALL,
  POLICY_STATUS_NEW,
  POLICY_STATUS_VARIANCE,
} from "../Constants/PolicyTranslationConstants";
import { ProcedureCodeDialogFilter } from "../Filters/FiltersComponent";

const ProcedureCodeDialog = ({
  open,
  onClose,
  onSave,
  availableProcedureCodes: defaultProcCodeList,
  selectedCC,
  setSelectedCC,
  selectedVariance,
  chips,
}) => {
  // seed local UI state once from props (no effect)
  const [dialogSelectedCC, setDialogSelectedCC] = useState(() => selectedCC);
  const [selectedVarianceOption, setSelectedVarianceOption] = useState(
    () => selectedVariance
  );

  // If you need to resync when parent props change without effects,
  // remount the dialog by keying it on the incoming props:
  // <Dialog key={`${open}-${selectedCC}-${selectedVariance}`} ...>

  const [checkedItems, setCheckedItems] = useState([]);

  const handleClose = () => {
    setDialogSelectedCC(selectedCC);
    setSelectedVarianceOption(selectedVariance);
    setCheckedItems([]);
    onClose();
  };

  const handleAddCodeList = () => {
    onSave(checkedItems, setCheckedItems);
    if (selectedCC !== dialogSelectedCC) {
      setSelectedCC(COVERAGE_CRITERIA_ALL);
    }
    handleClose();
  };

  // Consolidate once
  const consolidatedList = useMemo(() => {
    // 1) keep only active & editable
    const base = defaultProcCodeList.filter(
      (item) => !item.RuleEndDate && item.EditRule !== "EDIT"
    );

    // 2) consolidate by all fields except coverageCriteriaList
    const map = new Map();
    for (const item of base) {
      const { coverageCriteriaList, ...rest } = item;
      const key = JSON.stringify(rest);
      if (map.has(key)) {
        const existing = map.get(key);
        const exList = existing.coverageCriteriaList
          ? existing.coverageCriteriaList.split(/[,;]/)
          : [];
        const newList = coverageCriteriaList
          ? coverageCriteriaList.split(/[,;]/)
          : [];
        existing.coverageCriteriaList = Array.from(
          new Set([...exList, ...newList].filter(Boolean))
        ).join(",");
      } else {
        map.set(key, { ...item });
      }
    }
    // 3) de-dupe by code + diagnosis combo
    const uniq = new Map();
    for (const it of map.values()) {
      const k = `${it.code}-${it.DiagnosisListExpanded}`;
      if (!uniq.has(k)) uniq.set(k, it);
    }
    return Array.from(uniq.values());
  }, [defaultProcCodeList]);

  // Apply variance filter first (uses the raw list semantics from your code)
  const varianceFiltered = useMemo(() => {
    if (
      Array.isArray(selectedVarianceOption) &&
      (selectedVarianceOption.includes(POLICY_STATUS_NEW) ||
        selectedVarianceOption.includes(POLICY_STATUS_VARIANCE))
    ) {
      // match EditRule in the array
      return defaultProcCodeList.filter((item) =>
        selectedVarianceOption.includes(item.EditRule)
      );
    }
    if (selectedVarianceOption === "NOVARIANCE") {
      return defaultProcCodeList.filter(
        (item) => !item.EditRule || item.EditRule === "EDIT"
      );
    }
    // "All" → use consolidated view
    return consolidatedList;
  }, [selectedVarianceOption, defaultProcCodeList, consolidatedList]);

  // Then apply CC filter
  const filteredProcCodeList = useMemo(() => {
    if (dialogSelectedCC === COVERAGE_CRITERIA_ALL) return varianceFiltered;
    return varianceFiltered.filter((item) =>
      String(item.coverageCriteriaList || "")
        .split(/[,;]/)
        .includes(dialogSelectedCC)
    );
  }, [varianceFiltered, dialogSelectedCC]);

  // Filter handlers now just update local selection;
  // the list reacts via the memos above.
  const handleFilterCodeList = useCallback((cc) => {
    setDialogSelectedCC(cc);
  }, []);

  const handleFilterVarianceList = useCallback((v) => {
    setSelectedVarianceOption(Array.isArray(v) ? v[0] : v);
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontSize: "1.5rem" }}>
            Available Procedure Codes
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <ProcedureCodeDialogFilter
          selectedCC={selectedCC}
          setSelectedCC={setSelectedCC}
          selectedVarianceOption={selectedVarianceOption}
          chips={chips}
          dialogSelectedCC={dialogSelectedCC}
          setSelectedVarianceOption={setSelectedVarianceOption}
          setDialogSelectedCC={setDialogSelectedCC}
          handleFilterCodeList={handleFilterCodeList}
          handleFilterVarianceList={handleFilterVarianceList}
        />
        <CodeListSection
          filteredProcCodeList={filteredProcCodeList}
          checkedItems={checkedItems}
          setCheckedItems={setCheckedItems}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleAddCodeList} color="primary">
          Add To Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcedureCodeDialog;
